import React, { useState } from "react";
import Button from "components/ui/Button";
import { formatDistanceToNow } from "date-fns";
import Lightbox from "components/ui/Lightbox";

const COMMENT_STATUS_LABELS = {
  revisionSent: "Revision sent",
  revisionInProgress: "Revision in progress",
  editorUploaded: "Editor uploaded",
  revisionCompleted: "Revision completed",
};

const COMMENT_STATUS_STYLES = {
  revisionSent: "bg-red-50 text-red-600",
  revisionInProgress: "bg-blue-50 text-blue-600",
  editorUploaded: "bg-green-50 text-green-700",
  revisionCompleted: "bg-green-50 text-green-600",
};

const CommentsBox = ({ comments, approved = true, onApprove, imageId }) => {
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  const lightboxSlides = (comments || []).flatMap((comment) => {
    const slides = [];
    if (comment.requestedRevisionUrl) {
      slides.push({ src: comment.requestedRevisionUrl });
    }
    if (comment.attachment) slides.push({ src: comment.attachment });
    if (comment.responseRevisionUrl) {
      slides.push({ src: comment.responseRevisionUrl });
    }
    return slides;
  });

  const openLightbox = (src) => {
    const index = lightboxSlides.findIndex((slide) => slide.src === src);
    if (index !== -1) setLightboxIndex(index);
  };

  const sortedComments = [...(comments || [])].sort(
    (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
  );

  return (
    <div>
      {sortedComments.length > 1 && (
        <p className="text-xs font-semibold text-darkergray mb-3 pb-2 border-b border-lightgray">
          Comment history ({sortedComments.length})
        </p>
      )}
      {sortedComments.map((comment, index) => {
        const statusLabel =
          comment.status ||
          (comment.revisionId && !comment.responseRevisionId
            ? "revisionSent"
            : null);
        return (
          <div
            key={comment.id || index}
            className={
              index < sortedComments.length - 1
                ? "pb-3 mb-3 border-b border-lightgray"
                : ""
            }
          >
            <div className="flex items-start gap-2 mb-2 text-xs">
              <div className="w-6 h-6 rounded-full bg-darkergray flex items-center justify-center text-white font-semibold shrink-0">
                {comment.name?.[0]}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between flex-wrap gap-1">
                  <div>
                    <span className="text-xs font-semibold">{comment.name}</span>
                    <span className="ml-2 text-darkergray text-xs">
                      {comment.createdAt &&
                        formatDistanceToNow(new Date(comment.createdAt), {
                          addSuffix: true,
                        })}
                    </span>
                    {statusLabel && (
                      <span
                        className={`ml-2 text-xs px-2.5 py-0.5 rounded-full ${COMMENT_STATUS_STYLES[statusLabel] ||
                          "bg-green-50 text-green-600"
                          }`}
                        style={{ fontSize: "10px" }}
                      >
                        {COMMENT_STATUS_LABELS[statusLabel] ||
                          statusLabel
                            .replace(/([A-Z])/g, " $1")
                            .replace(/^./, (str) => str.toUpperCase())}
                      </span>
                    )}
                  </div>
                </div>

                {comment.requestedRevisionUrl && (
                  <div className="mt-2 rounded overflow-hidden border border-red-100 bg-red-50">
                    <img
                      src={comment.requestedRevisionUrl}
                      alt="Requested revision"
                      className="w-full max-h-40 object-cover cursor-pointer"
                      onClick={() =>
                        openLightbox(comment.requestedRevisionUrl)
                      }
                    />
                  </div>
                )}

                <div className="flex gap-2 justify-between">
                  <p className="text-xs mt-0.5 whitespace-pre-wrap flex-1">
                    {comment.text}
                  </p>

                  {comment.attachment && (
                    <div className="mt-1 w-12 h-12 shrink-0">
                      <img
                        src={comment.attachment}
                        alt="uploaded"
                        className="w-full h-full object-cover rounded cursor-pointer"
                        onClick={() => openLightbox(comment.attachment)}
                      />
                    </div>
                  )}
                </div>

                {comment.responseRevisionUrl &&
                  (comment.status === "editorUploaded" ||
                    comment.responseRevisionId) && (
                    <div className="mt-2 rounded overflow-hidden border border-green-200 bg-green-50">
                      <img
                        src={comment.responseRevisionUrl}
                        alt="Editor revision upload"
                        className="w-full max-h-48 object-cover cursor-pointer"
                        onClick={() =>
                          openLightbox(comment.responseRevisionUrl)
                        }
                      />
                    </div>
                  )}
              </div>
            </div>

            {!approved && (
              <Button
                className="mt-2 float-right outline-button"
                onClick={() => onApprove(imageId)}
              >
                Mark as Approved
              </Button>
            )}
          </div>
        );
      })}

      <Lightbox
        images={lightboxSlides}
        currentIndex={lightboxIndex}
        isOpen={lightboxIndex >= 0}
        onClose={() => setLightboxIndex(-1)}
      />
    </div>
  );
};

export default CommentsBox;
